import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
    Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
    ModalOverlay, Select, Stack, Textarea,
} from "@chakra-ui/react";
import { markReservationPaid } from "./paymentsApi";
import { PAYMENT_METHOD_OPTIONS } from "./paymentsConstants";
import PaymentField from "./PaymentField";

/**
 * Returns the empty initial form state.
 * @returns {Object}
 */
function buildInitialForm() {
    return { reservation_id: "", payment_intent_id: "", payment_method_type: "sepa_credit_transfer", note: "" };
}

/**
 * Validates the form before submission.
 * @param {Object} form - Form state
 * @returns {string|null} Error or null
 */
function validateForm(form) {
    const id = Number(form.reservation_id);
    if (!form.reservation_id || Number.isNaN(id) || id <= 0) return "L'ID de réservation est requis";
    return null;
}

/**
 * Builds the API payload from form state (omits empty fields).
 * @param {Object} form - Form state
 * @returns {Object}
 */
function buildPayload(form) {
    const payload = {};
    if (form.payment_intent_id?.trim()) payload.payment_intent_id = form.payment_intent_id.trim();
    if (form.payment_method_type) payload.payment_method_type = form.payment_method_type;
    if (form.note?.trim()) payload.note = form.note.trim();
    return payload;
}

/**
 * Submits the payload and surfaces toasts.
 * @param {Object} args - { form, setLoading, onMarked, onClose }
 */
async function submit({ form, setLoading, onMarked, onClose }) {
    const err = validateForm(form);
    if (err) { toast.error(err); return; }
    setLoading(true);
    try {
        await markReservationPaid(Number(form.reservation_id), buildPayload(form));
        toast.success("Réservation marquée comme payée");
        onMarked?.();
        onClose();
    } catch (error) {
        const msg = error?.response?.data || "Échec de l'action";
        toast.error(typeof msg === "string" ? msg : "Échec de l'action");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders the dialog body fields.
 * @param {Object} props - { form, onChange }
 * @returns {React.ReactElement}
 */
function DialogBody({ form, onChange }) {
    return (
        <Stack spacing="16px">
            <PaymentField label="ID Réservation" type="number" value={form.reservation_id} isRequired
                onChange={(v) => onChange("reservation_id", v)} helper="ID interne de la réservation à passer en PAID" />
            <PaymentField label="Stripe PaymentIntent ID" value={form.payment_intent_id} placeholder="pi_3NxYz..."
                onChange={(v) => onChange("payment_intent_id", v)}
                helper="Optionnel — utile si la réservation n'avait pas encore d'intent associé" />
            <PaymentField label="Méthode de paiement">
                <Select value={form.payment_method_type} borderRadius="12px"
                    onChange={(e) => onChange("payment_method_type", e.target.value)}>
                    {PAYMENT_METHOD_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                </Select>
            </PaymentField>
            <PaymentField label="Note interne" helper="Stocké dans l'audit trail (ex: 'Virement reçu hors webhook le 12/05')">
                <Textarea value={form.note} onChange={(e) => onChange("note", e.target.value)} rows={2} borderRadius="12px" />
            </PaymentField>
        </Stack>
    );
}

/**
 * Dialog allowing admins to manually flip a reservation to PAID
 * (use case: webhook lost between Stripe and the API).
 * @param {Object} props - { open, onClose, onMarked }
 * @returns {React.ReactElement}
 */
function MarkPaidDialog({ open, onClose, onMarked }) {
    const [form, setForm] = useState(buildInitialForm());
    const [loading, setLoading] = useState(false);
    useEffect(() => { if (open) setForm(buildInitialForm()); }, [open]);
    const onChange = (key, value) => setForm((f) => ({ ...f, [key]: value }));
    const onSubmit = () => submit({ form, setLoading, onMarked, onClose });
    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Marquer une réservation comme payée</ModalHeader>
                <ModalCloseButton />
                <ModalBody><DialogBody form={form} onChange={onChange} /></ModalBody>
                <ModalFooter gap="8px">
                    <Button variant="ghost" onClick={onClose}>Annuler</Button>
                    <Button colorScheme="brand" onClick={onSubmit} isLoading={loading}
                        loadingText="Action en cours...">Marquer comme payée</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default MarkPaidDialog;
